import { useState, useEffect, useMemo } from 'react';
import {
  PhoneIcon,
  UserGroupIcon,
  CalendarIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import { useAgency } from '../context/AgencyContext';
import { getAgencyAnalytics } from '../services/api';

interface DateRange {
  startDate: string;
  endDate: string;
}

interface CallMetrics {
  totalCalls: number;
  averageDuration: number;
  callsByHour: Array<{ hour: number; count: number }>;
  callsByDayOfWeek: Array<{ day: string; count: number }>;
  callsByOutcome: Array<{ outcome: string; count: number }>;
  callsOverTime: Array<{ date: string; count: number }>;
}

interface LeadMetrics {
  totalLeads: number;
  leadsByStatus: Array<{ status: string; count: number }>;
  conversionRate: number;
  leadsOverTime: Array<{ date: string; count: number }>;
  averageTimeToContact: number | null;
}

interface AppointmentMetrics {
  totalAppointments: number;
  appointmentsByStatus: Array<{ status: string; count: number }>;
  noShowRate: number;
  completionRate: number;
  appointmentsByService: Array<{ service: string; count: number }>;
  appointmentsOverTime: Array<{ date: string; count: number }>;
}

interface TopBusiness {
  id: string;
  name: string;
  totalCalls: number;
  totalLeads: number;
  totalAppointments: number;
}

interface AgencyAnalyticsData {
  dateRange: DateRange;
  totalBusinesses: number;
  calls: CallMetrics;
  leads: LeadMetrics;
  appointments: AppointmentMetrics;
  topBusinesses: TopBusiness[];
}

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}m ${secs}s`;
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

// Simple bar chart component
function BarChart({ data, maxValue }: { data: Array<{ label: string; value: number }>; maxValue?: number }) {
  const max = maxValue || Math.max(...data.map(d => d.value), 1);

  return (
    <div className="space-y-2">
      {data.map((item, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="w-20 text-sm text-gray-600 truncate" title={item.label}>
            {item.label}
          </div>
          <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary-500 rounded-full transition-all duration-300"
              style={{ width: `${(item.value / max) * 100}%` }}
            />
          </div>
          <div className="w-12 text-sm text-gray-900 text-right font-medium">
            {item.value}
          </div>
        </div>
      ))}
    </div>
  );
}

// Heatmap for hourly calls
function HourlyHeatmap({ data }: { data: Array<{ hour: number; count: number }> }) {
  const maxCount = Math.max(...data.map(d => d.count), 1);

  const getIntensity = (count: number) => {
    if (count === 0) return 'bg-gray-100';
    const ratio = count / maxCount;
    if (ratio > 0.8) return 'bg-primary-600';
    if (ratio > 0.6) return 'bg-primary-500';
    if (ratio > 0.4) return 'bg-primary-400';
    if (ratio > 0.2) return 'bg-primary-300';
    return 'bg-primary-200';
  };

  const formatHour = (hour: number) => {
    if (hour === 0) return '12am';
    if (hour === 12) return '12pm';
    if (hour < 12) return `${hour}am`;
    return `${hour - 12}pm`;
  };

  return (
    <div>
      <div className="grid grid-cols-12 gap-1 mb-1">
        {data.slice(0, 12).map((d, i) => (
          <div key={i} className="text-center text-xs text-gray-500">{formatHour(d.hour)}</div>
        ))}
      </div>
      <div className="grid grid-cols-12 gap-1 mb-3">
        {data.slice(0, 12).map((d, i) => (
          <div
            key={i}
            className={classNames(
              'h-8 rounded-sm flex items-center justify-center text-xs font-medium',
              getIntensity(d.count),
              d.count > maxCount * 0.6 ? 'text-white' : 'text-gray-700'
            )}
            title={`${formatHour(d.hour)}: ${d.count} calls`}
          >
            {d.count > 0 && d.count}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-12 gap-1 mb-1">
        {data.slice(12, 24).map((d, i) => (
          <div key={i} className="text-center text-xs text-gray-500">{formatHour(d.hour)}</div>
        ))}
      </div>
      <div className="grid grid-cols-12 gap-1">
        {data.slice(12, 24).map((d, i) => (
          <div
            key={i}
            className={classNames(
              'h-8 rounded-sm flex items-center justify-center text-xs font-medium',
              getIntensity(d.count),
              d.count > maxCount * 0.6 ? 'text-white' : 'text-gray-700'
            )}
            title={`${formatHour(d.hour)}: ${d.count} calls`}
          >
            {d.count > 0 && d.count}
          </div>
        ))}
      </div>
    </div>
  );
}

// Day of week chart
function DayOfWeekChart({ data }: { data: Array<{ day: string; count: number }> }) {
  const maxCount = Math.max(...data.map(d => d.count), 1);

  return (
    <div className="flex items-end justify-between h-32 gap-2">
      {data.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center">
          <div className="flex-1 w-full flex items-end">
            <div
              className="w-full bg-primary-500 rounded-t transition-all duration-300"
              style={{ height: `${(d.count / maxCount) * 100}%`, minHeight: d.count > 0 ? '4px' : '0' }}
            />
          </div>
          <div className="mt-2 text-xs text-gray-500">{d.day.slice(0, 3)}</div>
          <div className="text-xs font-medium text-gray-900">{d.count}</div>
        </div>
      ))}
    </div>
  );
}

export default function AnalyticsPage() {
  const { currentAgency } = useAgency();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analytics, setAnalytics] = useState<AgencyAnalyticsData | null>(null);
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d'>('30d');

  const dateRangeValues = useMemo(() => {
    const end = new Date();
    const start = new Date();

    switch (dateRange) {
      case '7d':
        start.setDate(end.getDate() - 7);
        break;
      case '30d':
        start.setDate(end.getDate() - 30);
        break;
      case '90d':
        start.setDate(end.getDate() - 90);
        break;
    }

    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
    };
  }, [dateRange]);

  useEffect(() => {
    if (!currentAgency) return;

    async function fetchAnalytics() {
      setLoading(true);
      setError(null);
      try {
        const data = await getAgencyAnalytics(
          currentAgency!.id,
          dateRangeValues.startDate,
          dateRangeValues.endDate
        );
        setAnalytics(data);
      } catch (err: any) {
        console.error('Failed to fetch analytics:', err);
        setError(err.message || 'Failed to load analytics');
      } finally {
        setLoading(false);
      }
    }

    fetchAnalytics();
  }, [currentAgency, dateRangeValues]);

  // Calculate derived metrics
  const derivedMetrics = useMemo(() => {
    if (!analytics) return null;

    const { calls, leads, appointments } = analytics;

    // Call success rate (completed vs all)
    const completedCalls = calls.callsByOutcome.find(o =>
      o.outcome === 'goodbye' || o.outcome === 'schedule_appointment'
    )?.count || 0;
    const callSuccessRate = calls.totalCalls > 0
      ? (completedCalls / calls.totalCalls) * 100
      : 0;

    // Lead capture rate (leads per call)
    const leadCaptureRate = calls.totalCalls > 0
      ? (leads.totalLeads / calls.totalCalls) * 100
      : 0;

    // Appointment booking rate (appointments per lead)
    const appointmentBookingRate = leads.totalLeads > 0
      ? (appointments.totalAppointments / leads.totalLeads) * 100
      : 0;

    // Cost per lead estimate (using Retell average costs)
    // Estimate: avg 2min call at ~$0.10/min = ~$0.20/call
    const avgCostPerCall = 0.20;
    const totalCost = calls.totalCalls * avgCostPerCall;
    const costPerLead = leads.totalLeads > 0
      ? totalCost / leads.totalLeads
      : 0;

    // Find peak hours
    const sortedHours = [...calls.callsByHour].sort((a, b) => b.count - a.count);
    const peakHours = sortedHours.slice(0, 3).filter(h => h.count > 0);

    return {
      callSuccessRate,
      leadCaptureRate,
      appointmentBookingRate,
      costPerLead,
      peakHours,
      totalCost,
    };
  }, [analytics]);

  if (!currentAgency) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-semibold text-gray-900">No Agency Selected</h3>
        <p className="mt-2 text-sm text-gray-500">Select an agency to view analytics.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-semibold text-red-600">Error Loading Analytics</h3>
        <p className="mt-2 text-sm text-gray-500">{error}</p>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-semibold text-gray-900">No Analytics Data</h3>
        <p className="mt-2 text-sm text-gray-500">No data available for the selected period.</p>
      </div>
    );
  }

  const stats = [
    {
      name: 'Total Calls',
      value: analytics.calls.totalCalls,
      icon: PhoneIcon,
      subtitle: `Avg ${formatDuration(analytics.calls.averageDuration)}`,
    },
    {
      name: 'Total Leads',
      value: analytics.leads.totalLeads,
      icon: UserGroupIcon,
      subtitle: `${formatPercent(derivedMetrics?.leadCaptureRate || 0)} capture rate`,
    },
    {
      name: 'Total Appointments',
      value: analytics.appointments.totalAppointments,
      icon: CalendarIcon,
      subtitle: `${formatPercent(analytics.appointments.completionRate)} completed`,
    },
    {
      name: 'Cost per Lead',
      value: `$${derivedMetrics?.costPerLead.toFixed(2) || '0.00'}`,
      icon: CurrencyDollarIcon,
      subtitle: `Est. total: $${derivedMetrics?.totalCost.toFixed(2) || '0.00'}`,
    },
  ];

  const rateCards = [
    {
      name: 'Call Success Rate',
      value: derivedMetrics?.callSuccessRate || 0,
      description: 'Calls that reached a successful conclusion',
      trend: derivedMetrics?.callSuccessRate && derivedMetrics.callSuccessRate > 50 ? 'up' : 'down',
    },
    {
      name: 'Lead Capture Rate',
      value: derivedMetrics?.leadCaptureRate || 0,
      description: 'Calls that resulted in a lead',
      trend: derivedMetrics?.leadCaptureRate && derivedMetrics.leadCaptureRate > 30 ? 'up' : 'down',
    },
    {
      name: 'Appointment Booking Rate',
      value: derivedMetrics?.appointmentBookingRate || 0,
      description: 'Leads that booked an appointment',
      trend: derivedMetrics?.appointmentBookingRate && derivedMetrics.appointmentBookingRate > 40 ? 'up' : 'down',
    },
    {
      name: 'Lead Conversion Rate',
      value: analytics.leads.conversionRate,
      description: 'Leads marked as converted',
      trend: analytics.leads.conversionRate > 20 ? 'up' : 'down',
    },
  ];

  return (
    <div>
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="mt-1 text-sm text-gray-500">
            Performance metrics for {currentAgency.name}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(['7d', '30d', '90d'] as const).map((range) => (
            <button
              key={range}
              onClick={() => setDateRange(range)}
              className={classNames(
                'px-3 py-1.5 text-sm font-medium rounded-md',
                dateRange === range
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              )}
            >
              {range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : '90 Days'}
            </button>
          ))}
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        {stats.map((stat) => (
          <div
            key={stat.name}
            className="relative overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:px-6 sm:py-6"
          >
            <dt>
              <div className="absolute rounded-md bg-primary-500 p-3">
                <stat.icon className="h-6 w-6 text-white" aria-hidden="true" />
              </div>
              <p className="ml-16 truncate text-sm font-medium text-gray-500">
                {stat.name}
              </p>
            </dt>
            <dd className="ml-16">
              <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
              <p className="text-sm text-gray-500">{stat.subtitle}</p>
            </dd>
          </div>
        ))}
      </div>

      {/* Rate Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        {rateCards.map((card) => (
          <div key={card.name} className="bg-white rounded-lg shadow p-5">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-500">{card.name}</h3>
              {card.trend === 'up' ? (
                <ArrowTrendingUpIcon className="h-5 w-5 text-green-500" />
              ) : (
                <ArrowTrendingDownIcon className="h-5 w-5 text-red-500" />
              )}
            </div>
            <p className="text-3xl font-bold text-gray-900">{formatPercent(card.value)}</p>
            <p className="mt-1 text-xs text-gray-500">{card.description}</p>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Busiest Hours Heatmap */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            <ClockIcon className="inline-block h-5 w-5 mr-2 text-gray-400" />
            Busiest Hours
          </h3>
          <HourlyHeatmap data={analytics.calls.callsByHour} />
          {derivedMetrics?.peakHours && derivedMetrics.peakHours.length > 0 && (
            <p className="mt-4 text-sm text-gray-500">
              Peak hours: {derivedMetrics.peakHours.map(h =>
                `${h.hour === 0 ? '12' : h.hour > 12 ? h.hour - 12 : h.hour}${h.hour >= 12 ? 'pm' : 'am'}`
              ).join(', ')}
            </p>
          )}
        </div>

        {/* Calls by Day of Week */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Calls by Day of Week</h3>
          <DayOfWeekChart data={analytics.calls.callsByDayOfWeek} />
        </div>
      </div>

      {/* Status Breakdown Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Call Outcomes */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Call Outcomes</h3>
          <BarChart
            data={analytics.calls.callsByOutcome.slice(0, 6).map(o => ({
              label: o.outcome.replace(/_/g, ' '),
              value: o.count,
            }))}
          />
        </div>

        {/* Lead Status */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Lead Status</h3>
          <BarChart
            data={analytics.leads.leadsByStatus.map(s => ({
              label: s.status,
              value: s.count,
            }))}
          />
        </div>

        {/* Appointment Status */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Appointment Status</h3>
          <BarChart
            data={analytics.appointments.appointmentsByStatus.map(s => ({
              label: s.status,
              value: s.count,
            }))}
          />
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">No-show rate:</span>
              <span className={classNames(
                'font-medium',
                analytics.appointments.noShowRate > 20 ? 'text-red-600' : 'text-green-600'
              )}>
                {formatPercent(analytics.appointments.noShowRate)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Top Clients */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Top Performing Clients</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Calls
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Leads
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Appointments
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lead Rate
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {analytics.topBusinesses.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500">
                    No client data available for this period
                  </td>
                </tr>
              ) : (
                analytics.topBusinesses.map((business) => {
                  const leadRate = business.totalCalls > 0
                    ? (business.totalLeads / business.totalCalls) * 100
                    : 0;
                  return (
                    <tr key={business.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {business.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                        {business.totalCalls}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                        {business.totalLeads}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                        {business.totalAppointments}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                        <span className={classNames(
                          'font-medium',
                          leadRate > 30 ? 'text-green-600' : leadRate > 15 ? 'text-yellow-600' : 'text-gray-500'
                        )}>
                          {formatPercent(leadRate)}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
